import { type CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { hasTranslationAudit } from '@/constants/sagaTypes'
import { formatTimestamp } from '@/utils/dateFormatters'
import { formatSagaEvent } from '@/utils/stringFormatters'
import {
  ArrowRightOutlined,
  InfoCircleOutlined,
  PartitionOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { BBDTag, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Button, Tooltip, theme } from 'antd'
import { getTimeElapsed } from '../utils/logUtils'
import { IssueCategoryCell } from './IssueCategoryCell'
import { issueCategoryLabel } from './LogDetailsPanel/categories'

const TAG_STYLE = { width: 'fit-content', transform: 'scale(0.9)' }

// Blend of the same pre-built light tints the app already uses for
// success/error backgrounds elsewhere (see .bg-success-dim/.bg-error-dim,
// styles/overrides.less) -- pastel like the plain Success/Failed BBDTag
// variants, rather than the dot's solid saturated colors, which read as a
// bold alert once stretched across a tag with text on it.
const PARTIAL_SUCCESS_FILL_GRADIENT =
  'linear-gradient(135deg, var(--theme-success-dim), var(--theme-error-dim))'

/**
 * The plain Success/Failed BBDTag variants get their border color from
 * antd's own computed colorSuccessBorder/colorErrorBorder tokens (via the
 * `success`/`error` props, resolved through antd's theme algorithm) rather
 * than our --theme-success/--theme-error CSS vars -- those tokens are
 * themselves desaturated relative to --theme-success/--theme-error in this
 * app's theme, so reading them via useToken() here (same hook BBDTag uses
 * internally) is what actually matches the border color those tags render,
 * in both light and dark mode.
 */
function PartialSuccessTag() {
  const { token } = theme.useToken()
  const borderGradient = `linear-gradient(135deg, ${token.colorSuccessBorder}, ${token.colorErrorBorder})`
  return (
    <BBDTag
      style={{
        ...TAG_STYLE,
        background: `${PARTIAL_SUCCESS_FILL_GRADIENT} padding-box, ${borderGradient} border-box`,
        border: '1px solid transparent',
        color: 'hsl(var(--foreground))',
      }}
    >
      Partial Success
    </BBDTag>
  )
}

export interface AuditTarget {
  logGroupId: string
  sagaId: string
  title: string
  routing?: CrossroadsLog['routing']
}

interface DetailGridContext {
  logGroupId?: string
  openAudit?: (target: AuditTarget) => void
}

/**
 * For group rows, returns the latest leaf child's data (by date).
 * For regular rows, returns the row's own data.
 *
 * Typed structurally rather than as ICellRendererParams so getRowStyle can
 * use it too -- `data` is undefined on a group row in both, and reading it
 * directly is what left grouped rows unstyled.
 */
export function getLatestLeafData(params: {
  data?: CrossroadsLog | null
  node?: {
    group?: boolean | null
    allLeafChildren?: ({ data?: CrossroadsLog | null } | null)[] | null
  } | null
}): CrossroadsLog | undefined {
  if (params.data) return params.data
  if (!params.node?.group) return undefined
  const children = params.node.allLeafChildren
  if (!children?.length) return undefined
  return children
    .map((n) => n?.data)
    .filter((d): d is CrossroadsLog => !!d)
    .sort(
      (a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime(),
    )[0]
}

/**
 * Returns the autoGroupColumnDef for the detail grid.
 * Renders the Event with the same saga event formatting as the hidden column.
 */
export function getAutoGroupColumnDef(): ColDef<CrossroadsLog> {
  return {
    headerName: 'Event',
    minWidth: 250,
    field: 'saga_id',
    filter: 'agSetColumnFilter',
    filterValueGetter: (params) => {
      const formatted = formatSagaEvent(
        params.data?.saga_type,
        params.data?.saga_id,
      )
      return formatted?.typeLabel
    },
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        if (params.node?.group === false) return null
        const data = getLatestLeafData(params)
        if (!data) return params.value
        const formatted = formatSagaEvent(data.saga_type, data.saga_id)
        if (!formatted) return null
        return (
          <Tooltip title={formatted.fullId || 'No saga ID'}>
            <Texto>{formatted.typeLabel}</Texto>
          </Tooltip>
        )
      },
    },
  }
}

/**
 * Returns column definitions for the detail grid.
 * Used with AG Grid's built-in master-detail pattern (detailGridOptions).
 */
export function getDetailColumnDefs(options?: {
  /** Whether rows should be grouped by Event (default true). When false,
   * the Event column is shown as a normal visible column instead of a
   * hidden row-group key. */
  groupByEventId?: boolean
  /** Variant for the Status chip shown on a warning log (default 'warning').
   * Supply Plan logs carry status=warning to mean a partial batch result -- a mix
   * of succeeded and failed events, not information on a success -- so that grid
   * passes 'partial_success' here for continuity with the batch-level
   * rollup_status/checkpoints tags. */
  warningVariant?: 'warning' | 'partial_success'
}): ColDef<CrossroadsLog>[] {
  const groupByEventId = options?.groupByEventId ?? true
  const warningVariant = options?.warningVariant ?? 'warning'
  return [
    {
      headerName: '',
      colId: 'audit',
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const ctx = params.context as DetailGridContext
        const data = getLatestLeafData(params)
        const sagaId = data?.saga_id
        if (!sagaId || !ctx?.logGroupId || !ctx.openAudit) return null
        if (!hasTranslationAudit(data?.saga_type)) return null
        const formatted = formatSagaEvent(data?.saga_type, sagaId)
        const title = formatted?.typeLabel ?? data?.title ?? 'Audit'
        return (
          <Tooltip title='View mapping audit'>
            <Button
              type='text'
              size='small'
              icon={<PartitionOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                ctx.openAudit?.({
                  logGroupId: ctx.logGroupId!,
                  sagaId,
                  title,
                  routing: data?.routing,
                })
              }}
            />
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Log Time',
      field: 'date',
      minWidth: 280,
      sortable: true,
      sort: 'desc',
      filter: 'agDateColumnFilter',
      cellDataType: 'dateTime',
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const data = getLatestLeafData(params)
        const dateValue = data?.date ?? params.value
        if (!dateValue) return null
        return (
          <Horizontal gap={8} alignItems='center'>
            <Texto>{formatTimestamp(dateValue)}</Texto>
            <Texto appearance='hint'>{getTimeElapsed(dateValue)}</Texto>
          </Horizontal>
        )
      },
    },
    {
      headerName: 'Event',
      field: 'saga_id',
      width: 160,
      filter: true,
      enableRowGroup: true,
      rowGroup: groupByEventId,
      hide: groupByEventId,
      valueGetter: (params) => {
        const formatted = formatSagaEvent(
          params.data?.saga_type,
          params.data?.saga_id,
        )
        if (!formatted) return null
        return formatted.shortId
          ? `${formatted.typeLabel} (${formatted.shortId})`
          : formatted.typeLabel
      },
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const formatted = formatSagaEvent(
          params.data?.saga_type,
          params.data?.saga_id,
        )
        if (!formatted) return null
        return (
          <Tooltip title={formatted.fullId || 'No saga ID'}>
            <Texto>
              {formatted.typeLabel}
              {formatted.shortId && (
                <Texto
                  appearance='hint'
                  style={{ marginLeft: 4, fontFamily: 'monospace' }}
                >
                  ({formatted.shortId})
                </Texto>
              )}
            </Texto>
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Log Details',
      field: 'title',
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const data = getLatestLeafData(params)
        return data?.detail?.message ?? data?.title ?? params.value ?? null
      },
    },
    {
      headerName: 'Issue',
      colId: 'issue',
      minWidth: 200,
      filter: 'agSetColumnFilter',
      valueGetter: (params) => issueCategoryLabel(params.data),
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => (
        <IssueCategoryCell log={getLatestLeafData(params)} />
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 130,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const data = getLatestLeafData(params)
        if (!data) return null
        if (data.retried) {
          return <BBDTag style={TAG_STYLE}>Retried</BBDTag>
        }
        // Superseded rather than resolved, so it reads like Retried: neutral
        // tag, no error/warning color. Gated on the status too -- a succeeded
        // log has nothing left to be irrelevant about.
        if (data.overridden && data.status !== 'succeeded') {
          return <BBDTag style={TAG_STYLE}>Overridden</BBDTag>
        }
        // Only the status decides. A low-severity failure is a failure the UI grades
        // gently, not a warning -- `_get_status` stopped conflating the two, and this
        // is its counterpart.
        if (data.status === 'warning') {
          if (warningVariant === 'partial_success') {
            return <PartialSuccessTag />
          }
          return (
            <BBDTag warning style={TAG_STYLE}>
              <WarningOutlined /> Warning
            </BBDTag>
          )
        } else if (data.status === 'succeeded') {
          return (
            <BBDTag success style={TAG_STYLE}>
              Success
            </BBDTag>
          )
        } else if (data.status === 'failed') {
          return (
            <BBDTag error style={TAG_STYLE}>
              Failed
            </BBDTag>
          )
        } else {
          return <BBDTag style={TAG_STYLE}>In Progress</BBDTag>
        }
      },
    },
    {
      headerName: 'Severity',
      field: 'severity',
      width: 120,
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        const data = getLatestLeafData(params)
        if (!data?.severity) return null
        if (data.status === 'succeeded') return null
        return (
          <SeverityTag
            severity={data.severity}
            style={{ width: 'fit-content' }}
          />
        )
      },
    },
    {
      headerName: 'Routing',
      field: 'routing',
      width: 180,
      cellRenderer: (params: ICellRendererParams<CrossroadsLog>) => {
        if (params.node?.group === false) return null
        const data = getLatestLeafData(params)
        const routing = data?.routing
        if (!routing) return null
        return (
          <Horizontal gap={6} alignItems='center'>
            <Texto>{routing.origin_tenant_name}</Texto>
            <ArrowRightOutlined
              style={{ color: 'var(--theme-text-hint)', fontSize: 11 }}
            />
            <Texto>{routing.destination_tenant_name}</Texto>
            <Tooltip
              title={`${routing.origin_instance_name} → ${routing.destination_instance_name}`}
            >
              <InfoCircleOutlined
                style={{ color: 'var(--theme-text-hint)', fontSize: 12 }}
              />
            </Tooltip>
          </Horizontal>
        )
      },
    },
    {
      headerName: 'Log ID',
      field: 'correlation_id',
      hide: true,
    },
  ]
}
